import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import { Doughnut, Bar } from "react-chartjs-2";
import "./sip-calculator.css";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
);

const formatINR = (amount) =>
  Number(amount.toFixed(0)).toLocaleString("en-IN");

function computeLumpsum(amount, rate, years) {
  if (amount <= 0 || rate <= 0 || years <= 0) return null;

  const r = rate / 100;
  const totalValue = amount * Math.pow(1 + r, years);
  const investedAmount = amount;
  const gain = totalValue - investedAmount;

  const growthData = [];
  for (let y = 1; y <= years; y++) {
    const yearValue = amount * Math.pow(1 + r, y);
    growthData.push({
      year: y,
      invested: amount,
      returns: Number((yearValue - amount).toFixed(0)),
    });
  }

  return {
    investedAmount: Number(investedAmount.toFixed(0)),
    gain: Number(gain.toFixed(0)),
    totalValue: Number(totalValue.toFixed(0)),
    growthData,
  };
}

export default function LumpsumCalculator() {
  const [amount, setAmount] = useState(300000);
  const [rate, setRate] = useState(12);
  const [years, setYears] = useState(10);

  const results = useMemo(
    () => computeLumpsum(amount, rate, years),
    [amount, rate, years]
  );

  const doughnutData = results && {
    labels: ["Invested Amount", "Est. Returns"],
    datasets: [
      {
        data: [results.investedAmount, results.gain],
        backgroundColor: ["#139bb2", "#a8e4ec"],
        borderWidth: 0,
        hoverOffset: 4,
      },
    ],
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "68%",
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          padding: 16,
          color: "#64748b",
          font: { size: 12, weight: "600" },
        },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ₹${formatINR(ctx.parsed)}`,
        },
      },
    },
  };

  const barData = results && {
    labels: results.growthData.map((d) => `Y${d.year}`),
    datasets: [
      {
        label: "Invested",
        data: results.growthData.map((d) => d.invested),
        backgroundColor: "#139bb2",
        borderRadius: 4,
        stack: "growth",
      },
      {
        label: "Returns",
        data: results.growthData.map((d) => d.returns),
        backgroundColor: "#a8e4ec",
        borderRadius: 4,
        stack: "growth",
      },
    ],
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ₹${formatINR(ctx.parsed.y)}`,
        },
      },
      legend: {
        position: "top",
        align: "end",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          color: "#64748b",
          font: { size: 12, weight: "600" },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: { color: "#94a3b8", font: { size: 11 } },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: { color: "#eef2f5" },
        ticks: {
          color: "#94a3b8",
          font: { size: 11 },
          callback: (v) => `₹${formatINR(v)}`,
        },
      },
    },
    interaction: { mode: "index", intersect: false },
  };

  return (
    <div className="sip-page">
      <section className="sip-hero">
        <div className="sip-hero-blob" aria-hidden="true" />
        <div className="sip-hero-inner">
          <p className="sip-eyebrow">LUMPSUM CALCULATOR</p>
          <h1 className="sip-hero-title">
            Grow a One-Time Investment into <span>Long-Term Wealth</span>
          </h1>
          <p className="sip-hero-lead">
            See how a single lumpsum investment can compound over time. Adjust
            amount, expected return, and tenure to project your future value.
          </p>
        </div>
      </section>

      <section className="sip-calc">
        <div className="sip-calc-inner">
          <div className="sip-calc-grid">
            <div className="sip-panel">
              <h2 className="sip-panel-title">Investment Details</h2>

              <div className="sip-field">
                <div className="sip-field-head">
                  <label htmlFor="lump-amount">Investment Amount</label>
                  <div className="sip-field-value sip-field-value--wide">
                    <span>₹</span>
                    <input
                      id="lump-amount"
                      type="number"
                      min={1000}
                      max={10000000}
                      step={1000}
                      value={amount}
                      onChange={(e) => setAmount(Number(e.target.value) || 0)}
                    />
                  </div>
                </div>
                <input
                  type="range"
                  className="sip-range"
                  min={1000}
                  max={2000000}
                  step={1000}
                  value={Math.min(amount, 2000000)}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  aria-label="Investment amount slider"
                />
                <div className="sip-range-ends">
                  <span>₹1,000</span>
                  <span>₹20,00,000</span>
                </div>
              </div>

              <div className="sip-field">
                <div className="sip-field-head">
                  <label htmlFor="lump-rate">Expected Return (p.a.)</label>
                  <div className="sip-field-value">
                    <input
                      id="lump-rate"
                      type="number"
                      min={1}
                      max={30}
                      step={0.5}
                      value={rate}
                      onChange={(e) => setRate(Number(e.target.value) || 0)}
                    />
                    <span>%</span>
                  </div>
                </div>
                <input
                  type="range"
                  className="sip-range"
                  min={1}
                  max={30}
                  step={0.5}
                  value={rate}
                  onChange={(e) => setRate(Number(e.target.value))}
                  aria-label="Expected return slider"
                />
                <div className="sip-range-ends">
                  <span>1%</span>
                  <span>30%</span>
                </div>
              </div>

              <div className="sip-field">
                <div className="sip-field-head">
                  <label htmlFor="lump-years">Time Period</label>
                  <div className="sip-field-value">
                    <input
                      id="lump-years"
                      type="number"
                      min={1}
                      max={40}
                      step={1}
                      value={years}
                      onChange={(e) => setYears(Number(e.target.value) || 0)}
                    />
                    <span>Yr</span>
                  </div>
                </div>
                <input
                  type="range"
                  className="sip-range"
                  min={1}
                  max={40}
                  step={1}
                  value={years}
                  onChange={(e) => setYears(Number(e.target.value))}
                  aria-label="Time period slider"
                />
                <div className="sip-range-ends">
                  <span>1 Yr</span>
                  <span>40 Yr</span>
                </div>
              </div>

              <div className="sip-cta-row">
                <Link to="/mutual-fund" className="sip-btn sip-btn--primary">
                  Explore Mutual Funds
                </Link>
                <Link to="/contact" className="sip-btn sip-btn--ghost">
                  Talk to an Advisor
                </Link>
              </div>
            </div>

            <div className="sip-panel sip-panel--results">
              <h2 className="sip-panel-title">Projected Returns</h2>

              {results ? (
                <>
                  <div className="sip-results-top">
                    <div className="sip-donut-wrap">
                      <Doughnut data={doughnutData} options={doughnutOptions} />
                    </div>
                    <div className="sip-stats">
                      <div className="sip-stat">
                        <span className="sip-stat-label">Invested Amount</span>
                        <strong>₹{formatINR(results.investedAmount)}</strong>
                      </div>
                      <div className="sip-stat">
                        <span className="sip-stat-label">Est. Returns</span>
                        <strong className="sip-stat--teal">
                          ₹{formatINR(results.gain)}
                        </strong>
                      </div>
                      <div className="sip-stat sip-stat--total">
                        <span className="sip-stat-label">Total Value</span>
                        <strong>₹{formatINR(results.totalValue)}</strong>
                      </div>
                    </div>
                  </div>

                  <p className="sip-summary">
                    An investment of <strong>₹{formatINR(amount)}</strong> for{" "}
                    <strong>
                      {years} {years > 1 ? "years" : "year"}
                    </strong>{" "}
                    at <strong>{rate}%</strong> p.a. can grow to{" "}
                    <strong>₹{formatINR(results.totalValue)}</strong>, including{" "}
                    <strong>₹{formatINR(results.gain)}</strong> in estimated
                    returns.
                  </p>
                </>
              ) : (
                <p className="sip-empty">
                  Enter valid positive values to see your projection.
                </p>
              )}
            </div>
          </div>

          {results && (
            <div className="sip-panel sip-panel--chart">
              <div className="sip-chart-head">
                <h2 className="sip-panel-title">Yearly Growth</h2>
                <p>Invested amount vs estimated returns over time</p>
              </div>
              <div className="sip-bar-wrap">
                <Bar data={barData} options={barOptions} />
              </div>
            </div>
          )}

          <div className="sip-related">
            <p>Try our other calculators</p>
            <div className="sip-related-links">
              <Link to="/SipCalculator">SIP</Link>
              <Link to="/RetirementCalculator">Retirement</Link>
              <Link to="/emi-calculator">EMI</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
